import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../../../api';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { normalizeValue } from '../../../../../utils/Texts';
import { getSessionUserId } from '../../../../../utils/SessionUser';
import { useLogos } from '../../../../../context/LogoProvider';

/**
 * CarrerasModal - Modal flotante para crear o editar carreras.
 * Ahora incluye soporte para subida de imágenes a S3 y obtención automática del instructor por sesión.
 */
export default function CarrerasModal({ open, mode, data, onClose, onSubmit, inline, onlyEditInstructor = false }) {
    const { getSystemConfig } = useLogos();
    const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
    const COURSE_IMAGE_UPLOAD_ENDPOINT = `${API_BASE}/upload/carrera-imagen`;

    const [form, setForm] = useState({
        nombre: '',
        descripcion: '',
        idInstructor: '',
        urlImagen: '',
        publicado: false
    });

    // Estados para subida de imagen
    const [imagePreview, setImagePreview] = useState(null);
    const [imageError, setImageError] = useState("");
    const [imageUploading, setImageUploading] = useState(false);

    // Estados para instructores
    const [instructores, setInstructores] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [personas, setPersonas] = useState([]);

    const loadInstructoresData = async () => {
        try {
            const [insRes, empRes, perRes] = await Promise.all([
                axios.get(ENDPOINTS.instructor),
                axios.get(ENDPOINTS.empleados),
                axios.get(ENDPOINTS.personas)
            ]);
            setInstructores(insRes.data || []);
            setEmpleados(empRes.data || []);
            setPersonas(perRes.data || []);
        } catch (error) {
            console.error('Error al cargar instructores en CarrerasModal:', error);
        }
    };

    const availableInstructores = useMemo(() => {
        return instructores.map(i => {
            const idInstructor = i.idInstructor ?? i.IdInstructor;
            const idEmpleado = i.idEmpleado ?? i.IdEmpleado;
            const emp = empleados.find(e => (e.idEmpleado ?? e.IdEmpleado) === idEmpleado);
            if (!emp) return { idInstructor, nombre: `Instructor ${idInstructor}` };
            const idPersona = emp.idPersona ?? emp.IdPersona;
            const p = personas.find(p => (p.idPersona ?? p.IdPersona) === idPersona);
            const nombre = p ? `${p.nombre ?? p.Nombre} ${p.apellidoPaterno ?? p.ApellidoPaterno} ${(p.apellidoMaterno ?? p.ApellidoMaterno) || ''}`.trim() : `Empleado ${idEmpleado}`;
            return { idInstructor, nombre };
        }).sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [instructores, empleados, personas]);

    useEffect(() => {
        if (open) {
            loadInstructoresData();
            if (mode === 'edit' && data) {
                setForm({
                    nombre: normalizeValue(data.nombre),
                    descripcion: normalizeValue(data.descripcion),
                    idInstructor: data.idInstructor || '',
                    activado: data.activado ?? true,
                    urlImagen: normalizeValue(data.urlImagen),
                    publicado: data.publicado ?? false
                });
                setImagePreview(null); // Reset preview on edit if not changed
            } else {
                setForm(prev => ({
                    ...prev,
                    nombre: '',
                    descripcion: '',
                    idInstructor: '',
                    activado: true,
                    urlImagen: '',
                    publicado: false
                }));
                fetchInstructorFromSession();
                setImagePreview(null);
            }
            setImageError("");
        }
    }, [mode, data, open]);

    const fetchInstructorFromSession = async () => {
        try {
            const sessionUserId = getSessionUserId();
            if (!sessionUserId) return;

            const resInstructor = await axios.get(ENDPOINTS.instructorByUsuario(sessionUserId));
            const instructor = Models.instructor(resInstructor.data);
            const idIns = instructor.idInstructor ?? instructor.IdInstructor;

            setForm(prev => ({ ...prev, idInstructor: idIns }));
        } catch (error) {
            console.error('Error al obtener instructor de sesión:', error);
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setImageError('Solo se permiten archivos de imagen.');
            return;
        }

        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
        if (file.size > maxMB * 1024 * 1024) {
            setImageError(`La imagen no debe superar los ${maxMB}MB.`);
            return;
        }

        setImageError("");
        const blobUrl = URL.createObjectURL(file);
        setImagePreview(blobUrl);

        try {
            setImageUploading(true);

            // Borrar imagen anterior si existía una temporal subida en esta sesión (opcional)
            // ...

            const formData = new FormData();
            formData.append('file', file);

            const resUpload = await axios.post(COURSE_IMAGE_UPLOAD_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true
            });

            const data = resUpload.data;
            if (!data.filePath) {
                throw new Error('La respuesta del servidor no incluye la ruta del archivo.');
            }

            setForm(prev => ({
                ...prev,
                urlImagen: data.filePath,
            }));
        } catch (error) {
            console.error('[CarrerasModal] Error al subir imagen:', error);
            setImageError(error.message || 'Error al subir la imagen al servidor.');
            setImagePreview(null);
            setForm(prev => ({ ...prev, urlImagen: "" }));
        } finally {
            setImageUploading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(form, mode);
    };

    if (!open) return null;

    return (
        <div className={inline ? "roles-inline-card" : "cc-course-modal-overlay"}>
            <div className={inline ? "" : "cc-course-modal"} style={inline ? { display: 'flex', flexDirection: 'column', gap: 0 } : {}}>
                <div className="cc-course-modal-title">
                    {mode === 'new' ? 'Nueva Carrera' : 'Editar Carrera'}
                </div>
                <p className="cc-course-modal-subtitle">
                    {mode === 'new' ? 'Proporciona los detalles básicos para crear una nueva carrera profesional.' : 'Actualiza la información de la carrera seleccionada.'}
                </p>

                <div className="cc-course-modal-body">
                    {onlyEditInstructor && (
                        <div className="cc-course-modal-field">
                            <label>Instructor <span style={{ color: 'red' }}>*</span></label>
                            <select
                                name="idInstructor"
                                value={form.idInstructor}
                                onChange={(e) => setForm(prev => ({ ...prev, idInstructor: Number(e.target.value) || '' }))}
                                style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '12px', fontSize: '0.9rem', fontFamily: 'inherit' }}
                            >
                                <option value="">-- Selecciona un instructor --</option>
                                {availableInstructores.map(i => (
                                    <option key={i.idInstructor} value={i.idInstructor}>{i.nombre}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="cc-course-modal-field">
                        <label>Nombre de la Carrera</label>
                        <input
                            type="text"
                            name="nombre"
                            value={form.nombre}
                            onChange={handleChange}
                            required
                            maxLength={100}
                            placeholder="Ej. Ingeniería de Software"
                            disabled={onlyEditInstructor}
                            style={onlyEditInstructor ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' } : {}}
                        />
                    </div>

                    <div className="cc-course-modal-field">
                        <label>Descripción</label>
                        <textarea
                            name="descripcion"
                            value={form.descripcion}
                            onChange={handleChange}
                            maxLength={500}
                            style={onlyEditInstructor ? { minHeight: '100px', display: 'block', width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '12px', fontSize: '0.9rem', fontFamily: 'inherit', backgroundColor: '#f1f5f9', cursor: 'not-allowed', color: '#64748b' } : { minHeight: '100px', display: 'block', width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '12px', fontSize: '0.9rem', fontFamily: 'inherit' }}
                            placeholder="Breve descripción de las metas y alcance de esta carrera..."
                            disabled={onlyEditInstructor}
                        />
                    </div>

                    {/* SECCIÓN DE SUBIDA DE IMAGEN (Estilo Cursos) */}
                    <div className="cc-course-modal-field">
                        <label>Portada de la Carrera</label>
                        <div
                            className={`file-uploader${form.urlImagen ? ' file-uploader--filled' : ''}${imageError ? ' file-uploader--error' : ''}${imageUploading ? ' file-uploader--uploading' : ''}`}
                            style={onlyEditInstructor ? { pointerEvents: 'none', opacity: 0.75, cursor: 'not-allowed' } : {}}
                        >
                            {form.urlImagen && (
                                <div className="file-uploader__preview">
                                    <img
                                        src={imagePreview || (form.urlImagen.startsWith('http') ? form.urlImagen : `${SERVER_URL}${form.urlImagen}`)}
                                        alt="Vista previa"
                                    />
                                </div>
                            )}

                            <label className="file-uploader__label">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    onClick={(e) => { e.target.value = ''; }}
                                    style={{ display: 'none' }}
                                    disabled={imageUploading || onlyEditInstructor}
                                />
                                <div className="file-uploader__icon">
                                    {imageUploading ? '⏳' : form.urlImagen ? '🔄' : '☁️'}
                                </div>
                                <div className="file-uploader__text">
                                    {imageUploading ? 'Subiendo...' : form.urlImagen ? 'Cambiar imagen' : 'Subir imagen de portada'}
                                </div>
                                <div className="file-uploader__hint">
                                    JPG, PNG o WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)
                                </div>
                            </label>
                        </div>
                        {imageError && <small style={{ color: '#dc2626', marginTop: 4, display: 'block' }}>{imageError}</small>}
                    </div>
                </div>

                <div className="cc-course-modal-actions" style={{ padding: '0 1.5rem 1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem', borderTop: 'none' }}>
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={onClose}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn-action"
                        onClick={handleSubmit}
                        disabled={(!onlyEditInstructor && !form.nombre) || imageUploading}
                        style={{ opacity: ((!onlyEditInstructor && !form.nombre) || imageUploading) ? 0.5 : 1, cursor: ((!onlyEditInstructor && !form.nombre) || imageUploading) ? 'not-allowed' : 'pointer' }}
                    >
                        {mode === 'new' ? 'Guardar' : 'Actualizar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
